import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Towns = () => {
  const [towns, setTowns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTowns = async () => {
      try {
        const response = await axios.get(`${API}/towns`);
        setTowns(response.data);
      } catch (error) {
        console.error("Error fetching towns:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTowns();
  }, []);

  const coreMarkets = towns.filter(t => 
    ["fargo-nd", "west-fargo-nd", "moorhead-mn"].includes(t.slug)
  );
  const nearbyTowns = towns.filter(t => 
    !["fargo-nd", "west-fargo-nd", "moorhead-mn"].includes(t.slug)
  );

  return (
    <div data-testid="service-areas-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Service Areas
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
              Communities We Serve
            </h1>
            <p className="text-lg text-[#5C564D] leading-relaxed max-w-2xl mx-auto">
              Legacy Liquidators LLC provides estate sale services throughout Fargo-Moorhead and surrounding communities within about 60 miles.
            </p>
          </div>
        </div>
      </section>

      {/* Core Markets */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-8">
            Core Service Area
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5C564D]">Loading service areas...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {coreMarkets.map((town) => (
                <Link
                  key={town.id}
                  to={`/service-areas/${town.slug}`}
                  className="bg-[#2C4C3B] p-8 rounded-lg text-white group hover:bg-[#1E3629] transition-all duration-300"
                  data-testid={`core-town-${town.slug}`}
                >
                  <MapPin weight="duotone" className="w-8 h-8 text-[#C5A059] mb-4" />
                  <h3 className="text-2xl font-serif font-medium mb-2">
                    {town.name}, {town.state}
                  </h3>
                  <p className="text-white/80 text-sm mb-4">{town.distance_from_fargo}</p>
                  <span className="inline-flex items-center gap-2 text-[#C5A059] font-medium group-hover:gap-3 transition-all">
                    View Details
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Link>
              ))}
            </div>
          )}

          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-8">
            Surrounding Communities
          </h2>
          
          {!loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {nearbyTowns.map((town) => (
                <Link
                  key={town.id}
                  to={`/service-areas/${town.slug}`}
                  className="bg-[#F7F5F0] border border-[#E2DCD0] p-6 rounded-lg group hover:shadow-md transition-all duration-300"
                  data-testid={`nearby-town-${town.slug}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-serif font-medium text-[#1C1914] group-hover:text-[#2C4C3B] transition-colors">
                        {town.name}, {town.state}
                      </h3>
                      <p className="text-[#5C564D] text-sm">{town.distance_from_fargo}</p>
                    </div>
                    <MapPin weight="duotone" className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#2C4C3B] text-sm font-medium group-hover:gap-3 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#E2DCD0] rounded-lg p-12 text-center">
            <MapPin weight="duotone" className="w-16 h-16 text-[#2C4C3B] mx-auto mb-6" />
            <h2 className="text-2xl font-serif font-medium text-[#1C1914] mb-4">
              Serving the Fargo-Moorhead Region
            </h2>
            <p className="text-[#5C564D] max-w-2xl mx-auto mb-6">
              Our service area extends approximately 60 miles from Fargo, covering communities in both North Dakota and western Minnesota. From Detroit Lakes to Wahpeton, we're here to help families with compassionate estate sale services.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {towns.map((town) => (
                <span key={town.id} className="bg-white px-3 py-1 rounded-full text-sm text-[#5C564D]">
                  {town.name}, {town.state}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#1C1914]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-medium text-white mb-6">
            Need Estate Sale Services in Your Area?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Contact us to confirm we serve your location and schedule a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="areas-cta-phone"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="areas-cta-contact"
            >
              Request a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Towns;
